import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Navbar from '../components/Navbar';
import DepartmentCard from './cards/DepartmentCard';
import UserCard from './cards/UserCard';
import {
  DEPARTMENT_LIST_API,
  DEPARTMENT_ADD_API,
  DEPARTMENT_UPDATE_API,
  CLIENT_USER_ADD_API,
  CLIENT_USERS_LIST_API,
} from '../utils/api';
import axios from 'axios';

function TeamManagement() {
  // ---------- auth ----------
  const token = localStorage.getItem('token');
  const parsed = token ? JSON.parse(atob(token)) : null;
  const customer_id = parsed?.customer_id;
  const authHeaders = useMemo(() => ({ Authorization: token || '' }), [token]);

  // ---------- state ----------
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedDeptId, setSelectedDeptId] = useState(null);

  const [newDeptName, setNewDeptName] = useState('');
  const [newUser, setNewUser] = useState({
    name: '',
    mobile: '',
    role: '',
    password: '',
    department_id: '',
  });

  const [searchTerm, setSearchTerm] = useState('');

  // UX states
  const [loadingDepts, setLoadingDepts] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [savingDept, setSavingDept] = useState(false);
  const [updatingDept, setUpdatingDept] = useState(false);
  const [savingUser, setSavingUser] = useState(false);

  // ---------- helpers ----------
  const normalizeDepartments = (arr) =>
    (arr || []).map((d) => ({
      id: Number(d.id ?? d.department_id),
      name: d.name ?? d.department_name,
      customer_id: Number(d.customer_id ?? 0),
      status: Number(d.status ?? 1),
    }));

  const selectedDept = useMemo(
    () => departments.find((d) => d.id === selectedDeptId) || null,
    [departments, selectedDeptId]
  );

  // ---------- data loaders ----------
  const loadDepartments = useCallback(async () => {
    if (!token || !customer_id) return;
    try {
      setLoadingDepts(true);
      const res = await axios.post(
        DEPARTMENT_LIST_API,
        { customer_id, status: 'all' },
        { headers: authHeaders }
      );
      if (res.data?.success) {
        const list = normalizeDepartments(res.data.rows || res.data.departments);
        setDepartments(list);
        // Auto-select: prefer last selected if still present, else first active, else first
        if (!selectedDeptId && list.length) {
          const firstActive = list.find((x) => x.status === 1);
          setSelectedDeptId(firstActive?.id ?? list[0].id);
        }
      } else {
        alert(res.data?.message || 'Failed to fetch departments');
      }
    } catch (e) {
      console.error('❌ Department fetch error:', e);
      alert('Error loading departments');
    } finally {
      setLoadingDepts(false);
    }
  }, [token, customer_id, authHeaders, selectedDeptId, DEPARTMENT_LIST_API]);

  const loadUsers = useCallback(
    async (department_id) => {
      if (!department_id || !customer_id) return;
      try {
        setLoadingUsers(true);
        const res = await axios.post(
          CLIENT_USERS_LIST_API,
          { customer_id, department_id, token },
          { headers: authHeaders }
        );
        if (res.data?.success) {
          setUsers(res.data.users || []);
        } else {
          setUsers([]);
          console.warn(res.data?.message || 'No users found');
        }
      } catch (e) {
        console.error('❌ Users fetch error:', e);
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    },
    [customer_id, token, authHeaders]
  );

  // Initial loads
  useEffect(() => {
    loadDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load users when department changes
  useEffect(() => {
    if (selectedDeptId) loadUsers(selectedDeptId);
  }, [selectedDeptId, loadUsers]);

  // ---------- actions: department ----------
  const handleSelectDepartment = (dept) => {
    setSelectedDeptId(dept?.id || null);
  };

  const handleAddDepartment = async () => {
    const name = (newDeptName || '').trim();
    if (!name) return;

    try {
      setSavingDept(true);
      const res = await axios.post(
        DEPARTMENT_ADD_API,
        { name, customer_id },
        { headers: authHeaders }
      );

      if (res.data?.success) {
        const dep = res.data.department;
        const normalized = normalizeDepartments([dep])[0];
        setDepartments((prev) => [...prev, normalized]);
        setNewDeptName('');
        setSelectedDeptId(normalized.id);
      } else {
        alert(res.data?.message || 'Failed to add department');
      }
    } catch (e) {
      console.error('❌ Add department error:', e);
      alert('Error adding department');
    } finally {
      setSavingDept(false);
    }
  };

  const handleUpdateDepartment = async (id, newName) => {
    const name = (newName || '').trim();
    if (!id || !name) {
      alert('Missing department ID or name');
      return;
    }
    try {
      setUpdatingDept(true);
      const res = await axios.post(
        DEPARTMENT_UPDATE_API,
        { id, name },
        { headers: authHeaders }
      );

      if (res.data?.success) {
        setDepartments((prev) => prev.map((d) => (d.id === id ? { ...d, name } : d)));
        if (selectedDeptId === id) {
          // keep local selected label in sync
          // (derived from departments, so not strictly required)
        }
      } else {
        alert(res.data?.message || 'Update failed');
      }
    } catch (e) {
      console.error('❌ Update department error:', e);
      alert('Error updating department');
    } finally {
      setUpdatingDept(false);
    }
  };

  const handleToggleDepartmentStatus = async (id, newStatus) => {
    if (!id) return;
    try {
      const res = await axios.post(
        DEPARTMENT_UPDATE_API,
        { id, status: newStatus },
        { headers: authHeaders }
      );

      if (res.data?.success) {
        setDepartments((prev) =>
          prev.map((d) => (d.id === id ? { ...d, status: newStatus } : d))
        );
        // If deactivating selected dept, keep it selected (your call) or auto-switch:
        if (selectedDeptId === id && newStatus === 0) {
          const next = departments.find((x) => x.status === 1 && x.id !== id) || departments[0];
          if (next) setSelectedDeptId(next.id);
        }
      } else {
        alert(res.data?.message || 'Status update failed');
      }
    } catch (e) {
      console.error('❌ Status toggle error:', e);
      alert('Error updating status');
    }
  };

  // ---------- actions: user ----------
  const handleAddUser = async () => {
    if (
      !selectedDeptId ||
      (newUser.name || '').trim().length < 3 ||
      (newUser.mobile || '').trim().length !== 10 ||
      !newUser.role
    ) {
      alert('Please fill all fields correctly and select a department');
      return;
    }

    try {
      setSavingUser(true);
      const password =
        (newUser.password || '').trim() || (newUser.mobile?.slice(-4) + '@123');

      const res = await axios.post(
        CLIENT_USER_ADD_API,
        {
          name: newUser.name.trim(),
          mobile: newUser.mobile.trim(),
          role: newUser.role,
          password,
          department_id: selectedDeptId,
          customer_id,
          token,
        },
        { headers: authHeaders }
      );

      if (res.data?.success) {
        setUsers((prev) => [...prev, res.data.user]);
        setNewUser({ name: '', mobile: '', role: '', password: '', department_id: '' });
      } else {
        alert(res.data?.message || 'Failed to add user');
      }
    } catch (e) {
      console.error('❌ Add user error:', e);
      alert('Error adding user');
    } finally {
      setSavingUser(false);
    }
  };

  // ---------- computed ----------
  const filteredUsers = useMemo(() => {
    const term = (searchTerm || '').toLowerCase();
    return users.filter(
      (u) =>
        (u.name || '').toLowerCase().includes(term) ||
        (u.mobile || '').toLowerCase().includes(term)
    );
  }, [users, searchTerm]);

  return (
    <>
      <Navbar />
      <div className="container mt-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h3 className="mb-0">👥 Team Management</h3>
          <small className="text-muted">
            {loadingDepts ? 'Loading departments…' : `Departments: ${departments.length}`}
          </small>
        </div>

        <div className="row">
          {/* Left: Departments */}
          <div className="col-lg-4 mb-4">
            <DepartmentCard
              loading={loadingDepts}
              saving={savingDept || updatingDept}
              departments={departments}
              selectedDeptId={selectedDeptId}
              onSelect={handleSelectDepartment}
              newDeptName={newDeptName}
              setNewDeptName={setNewDeptName}
              onAddDepartment={handleAddDepartment}
              onUpdateDepartment={handleUpdateDepartment}
              onToggleDepartmentStatus={handleToggleDepartmentStatus}
            />
          </div>

          {/* Right: Users */}
          <div className="col-lg-8">
            <UserCard
              selectedDept={selectedDept}
              loadingUsers={loadingUsers}
              users={filteredUsers}
              newUser={newUser}
              setNewUser={setNewUser}
              onAddUser={handleAddUser}
              reloadUsers={() => selectedDeptId && loadUsers(selectedDeptId)}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              savingUser={savingUser}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default TeamManagement;
